import { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useProductTypes } from "@/lib/productTypes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const trig = "h-11 rounded-md text-sm w-full";

/**
 * 2026-06 · 产品列表手机端筛选抽屉 —— 桌面那一整行筛选控件在手机上放不下，
 * 这里收进底部抽屉：类型/大类/小类/品牌、创建日期、工厂成本、售价、显示货币。
 * 触控高度统一 44px，单手可操作。
 */
export const MobileProductFilters = ({
  filters, setFilter, options, currency, switchCurrency, showCurrency = true,
  total, onShowAll,
}) => {
  const { t } = useLanguage();
  const productTypes = useProductTypes();
  const [open, setOpen] = useState(false);

  const subEntries = options.subcategories || [];
  const subOptions = [...new Set((filters.category
    ? subEntries.filter((sc) => sc.category === filters.category)
    : subEntries).map((sc) => sc.name).filter(Boolean))].sort();

  const activeCount = [
    filters.product_type, filters.category, filters.subcategory, filters.brand,
    filters.date_from, filters.date_to, filters.cost_min, filters.cost_max,
    filters.price_min, filters.price_max,
  ].filter((v) => String(v || "").trim()).length;

  const reset = () => {
    ["product_type", "category", "subcategory", "brand", "date_from", "date_to",
      "cost_min", "cost_max", "price_min", "price_max"].forEach((k) => setFilter(k, ""));
  };

  const pick = (key, label, opts, disabled = false) => (
    <div className="space-y-1">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <Select value={filters[key] || "__all__"} onValueChange={(v) => setFilter(key, v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid={`m-filter-${key}`} disabled={disabled}
          className={`${trig} ${filters[key] ? "border-brand text-brand" : ""}`}>
          <SelectValue placeholder={t("common.all")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t("common.all")}</SelectItem>
          {opts.map(({ value, text }) => <SelectItem key={value} value={value}>{text}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const range = (label, keys, extra = null) => (
    <div className="space-y-1">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <div className="flex items-center gap-1.5">
        {extra}
        <Input type="number" inputMode="decimal" placeholder={t("products.min")}
          value={filters[keys[0]] || ""} onChange={(e) => setFilter(keys[0], e.target.value)}
          data-testid={`m-filter-${keys[0]}`} className="h-11 rounded-md text-sm" />
        <span className="text-muted-foreground text-xs">–</span>
        <Input type="number" inputMode="decimal" placeholder={t("products.max")}
          value={filters[keys[1]] || ""} onChange={(e) => setFilter(keys[1], e.target.value)}
          data-testid={`m-filter-${keys[1]}`} className="h-11 rounded-md text-sm" />
      </div>
    </div>
  );

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
        data-testid="m-products-filter-button"
        className={`md:hidden h-9 rounded-md shrink-0 gap-1 ${activeCount ? "border-brand text-brand" : ""}`}>
        <SlidersHorizontal size={15} />
        {activeCount > 0 && (
          <span className="px-1.5 rounded-full bg-brand text-white text-[10px] font-bold leading-4"
            data-testid="m-products-filter-count">{activeCount}</span>
        )}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="h-[88vh] overflow-y-auto"
          data-testid="m-products-filter-sheet">
          <SheetHeader>
            <SheetTitle>{t("products.filters")}</SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-3 pb-28">
            {pick("product_type", t("products.productType"),
              productTypes.map((pt) => ({ value: pt.code, text: pt.name_zh || pt.name_en || pt.code })))}
            {pick("category", t("products.category"),
              (options.categories || []).map((o) => ({ value: o, text: o })))}
            {pick("subcategory", t("products.subcategory"),
              subOptions.map((o) => ({ value: o, text: o })), subOptions.length === 0)}
            {pick("brand", t("products.brand"),
              (options.brands || []).map((o) => ({ value: o, text: o })))}
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t("products.creationDateFilter")}</p>
              <div className="flex items-center gap-1.5">
                <Input type="date" value={filters.date_from || ""} onChange={(e) => setFilter("date_from", e.target.value)}
                  data-testid="m-filter-date-from" className="h-11 rounded-md font-mono text-xs" />
                <span className="text-muted-foreground text-xs">–</span>
                <Input type="date" value={filters.date_to || ""} onChange={(e) => setFilter("date_to", e.target.value)}
                  data-testid="m-filter-date-to" className="h-11 rounded-md font-mono text-xs" />
              </div>
            </div>
            {range(t("products.factoryCostFilter"), ["cost_min", "cost_max"], (
              <Select value={filters.cost_currency || "RMB"} onValueChange={(v) => setFilter("cost_currency", v)}>
                <SelectTrigger data-testid="m-filter-cost-currency" className="h-11 w-[84px] rounded-md text-xs shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RMB">RMB</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            ))}
            {range(`${t("products.sellingPriceFilter")} (${currency === "CAD" ? "C$" : "US$"})`, ["price_min", "price_max"])}
            {showCurrency && (
              <div className="space-y-1">
                <p className="text-[11px] text-muted-foreground">{t("quote.paramCurrency")}</p>
                <div className="flex rounded-md border border-border overflow-hidden">
                  {["USD", "CAD"].map((c) => (
                    <button key={c} type="button" onClick={() => switchCurrency(c)}
                      data-testid={`m-currency-${c.toLowerCase()}`}
                      className={`flex-1 h-11 font-mono text-sm font-bold transition-colors ${
                        currency === c ? "bg-brand text-white" : "text-muted-foreground"}`}>
                      {c === "USD" ? "US$" : "C$"}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button variant="outline" onClick={() => { onShowAll(); }}
              data-testid="m-products-show-all" className="w-full h-11 rounded-md">
              {t("products.showAll")}
            </Button>
          </div>
          <div className="fixed left-0 right-0 bottom-0 p-3 pb-[calc(env(safe-area-inset-bottom)+12px)] bg-card border-t border-border flex gap-2">
            <Button variant="outline" onClick={reset} data-testid="m-products-filter-reset"
              className="h-12 rounded-md flex-1">
              <X size={15} /> {t("common.reset")}
            </Button>
            <Button onClick={() => setOpen(false)} data-testid="m-products-filter-apply"
              className="h-12 rounded-md flex-1 bg-brand hover:bg-brand-hover">
              {total} {t("products.recordsFound")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
